import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../../../forms/Button';
import SortableImageGrid from './DNDCROP/SortableImageGrid';
import CropperModal from './DNDCROP/CropperModal';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteImage,
  setBatchId,
  setImages,
} from '../../../../redux/slice/sketchSlice';
import {
  setActiveBookId,
  setUploadingImages,
} from '../../../../redux/slice/stepSlice';
import { saveStep } from '../../../../services/stepService';
import { profileDetails } from '../../../../services/ProfileService';
import BookSelectionModal from '../../../modalcontent/BookSelectionModal';
import {
  removeBookImage,
  reorderBooks,
} from '../../../../services/bookService';
import StepProgressView from '../../../common/StepProgressView';
import FileUploader from '../../../common/UppyUploader';
import { getActiveStep } from "../../../../services/commonService/";
import { markStepAsVisited } from '../../../../redux/slice/profileSlice';
import { run } from '../../../../services/runpodService';
import { processAllImagesForRun } from '../../../../util/imageCropUploadUtils';

// Image Limit Modal Component - Enhanced Design
const ImageLimitModal = ({ isOpen, onClose, currentCount, limit = 20 }) => {
  if (!isOpen) return null;

  const excess = currentCount - limit;

  return (
    <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 relative overflow-hidden'>
        {/* Decorative gradient header */}
        <div className='h-2 bg-gradient-to-r from-pink-500 to-purple-500'></div>

        {/* Content */}
        <div className='p-8'>
          <div className='text-center space-y-4'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full mb-2'>
              <i className='fa-solid fa-images text-2xl text-md-pink'></i>
            </div>
            <h2 className='text-2xl font-bold text-gray-900'>
              Oops, that's a few too many!
            </h2>
            <p className='text-gray-600 text-lg leading-relaxed'>
              Your coloring book holds{' '}
              <span className='font-bold text-gray-900'>
                {limit} special moments
              </span>
              .
            </p>
            <p className='text-gray-700 leading-relaxed'>
              You've selected{' '}
              <span className='font-bold text-md-pink text-lg'>
                {currentCount}
              </span>{' '}
              images—please remove{' '}
              <span className='font-bold text-md-pink text-lg'>{excess}</span>{' '}
              to continue.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className='px-8 pb-8'>
          <button
            onClick={onClose}
            className='w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-md-pink hover:to-purple-600 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2'
          >
            <i className='fa-solid fa-check mr-2'></i>
            Got it, I'll remove some images
          </button>
        </div>
      </div>
    </div>
  );
};

// Less Than 20 Images Modal Component - Enhanced Design
const LessThan20Modal = ({
  isOpen,
  onClose,
  onContinue,
  onUploadMore,
  currentCount,
  limit = 20,
}) => {
  if (!isOpen) return null;

  const remaining = limit - currentCount;

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const formattedFiles = files.map((file) => ({
        file: file,
        url: URL.createObjectURL(file),
        filename: file.name,
        publicId: null,
      }));
      onUploadMore(formattedFiles);
    }
    event.target.value = '';
  };

  return (
    <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-auto max-h-[95%]'>
        <div className='p-8'>
          <div className='text-center space-y-4'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full mb-2'>
              <i className='fa-solid fa-image text-2xl text-md-pink'></i>
            </div>
            <h2 className='text-xl md:text-2xl font-bold text-gray-900'>
              Not quite there yet!
            </h2>
            <p className='text-gray-600 text-base md:text-lg leading-relaxed'>
              Your coloring book holds{' '}
              <span className='font-bold text-gray-900'>
                {limit} special moments.
              </span>
            </p>
            <p className='text-gray-700 leading-relaxed'>
              You've selected{' '}
              <span className='font-bold text-md-pink text-base md:text-lg'>
                {currentCount}
              </span>{' '}
              images—add{' '}
              <span className='font-bold text-md-pink text-base md:text-lg'>
                {remaining}
              </span>{' '}
              more to fill your book, or leave the extra pages blank for
              free-style coloring fun.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className='px-8 pb-8 space-y-3'>
          <input
            type='file'
            id='modal-file-input'
            multiple
            accept='image/*'
            onChange={handleFileSelect}
            className='hidden'
          />

          <label
            htmlFor='modal-file-input'
            className='w-full bg-md-pink text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center focus:outline-none text-sm md:text-base'
          >
            <i className='fa-solid fa-upload mr-2'></i>
            <span>Upload More Images</span>
          </label>

          <button
            onClick={onContinue}
            className='w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-200 focus:outline-none text-sm md:text-base'
          >
            <i className='fa-solid fa-arrow-right mr-2'></i>
            <span>Continue Anyway</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const PlaceOrderContent = () => {
  const [croppingImage, setCroppingImage] = useState(null);
  const [deletedFilenames, setDeletedFilenames] = useState([]);
  const [apiprofileDetails, setProfileDetails] = useState({});
  const [isOpenBookSelectionModal, setIsOpenBookSelectionModal] = useState(false);
  const [showImageLimitModal, setShowImageLimitModal] = useState(false);
  const [showLessThan20Modal, setShowLessThan20Modal] = useState(false);
  const [modalImageCount, setModalImageCount] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);
  const images = useSelector((state) => state.sketch.images);
  const coverImages = useSelector((state) => state.sketch.coverImages);
  const batchId = useSelector((state) => state.sketch.batchId);
  const { activeBookId, uploadingImagesBatch, uploadingImages } = useSelector(
    (state) => state.stepFlow
  );
  const { session } = useSelector((state) => state.authProvider);

  const stepNumber = getActiveStep();

  useEffect(() => {
    if (!batchId) {
      dispatch(setBatchId(uuidv4()));
    }
  }, [dispatch, batchId]);

  const handleCropSave = ({ blob, url, cropData }) => {
    // Validate required parameters
    if (!blob || !url || !cropData) {
      console.error('Invalid crop data received:', {
        blob: !!blob,
        url: !!url,
        cropData: !!cropData,
      });
      toast.error('Failed to save cropped image. Please try again.');
      return;
    }

    if (!croppingImage) {
      console.error('No cropping image reference found');
      return;
    }

    try {
      const getFilename = (img) => {
        if (img.originalFilename) return img.originalFilename;
        if (img.file?.name) return img.file.name;
        if (img.filename) return img.filename;
        return `cropped-image-${Date.now()}.jpg`;
      };

      if (!(blob instanceof Blob)) {
        throw new Error('Invalid blob object received');
      }

      const updated = images.map((img) => {
        const cond = !!img._id
          ? img._id === croppingImage._id
          : img.id === croppingImage.id;

        if (cond) {
          const filename = getFilename(img);

          try {
            const newFile = new File([blob], filename, {
              type: blob.type || 'image/jpeg',
              lastModified: Date.now(),
            });

            return {
              ...img,
              url,
              file: newFile,
              meta: null,
              AIUrl: null,
              cropData,
              originalUrl: img.originalUrl || img.url,
              originalFilename: img.originalFilename || img.filename || filename,
              lastModified: Date.now(),
              isCropped: true,
            };
          } catch (fileError) {
            console.error('Error creating File object:', fileError);
            throw new Error(`Failed to create file: ${fileError.message}`);
          }
        }
        return img;
      });

      if (!updated || updated.length !== images.length) {
        throw new Error('Failed to update images array properly');
      }

      dispatch(setImages(updated));
      setCroppingImage(null);

    } catch (error) {
      console.error('Error in handleCropSave:', error);
      toast.error(`Failed to save cropped image: ${error.message}`);
    }
  };

  const handleImageUpload = (files) => {
    const newImages = files.map((file) => ({
      id: uuidv4(),
      url: file.url,
      publicId: file.publicId,
      file: file.file?.data || file.file,
      filename: file.filename,
      originalUrl: file.url,
      cropData: null,
    }));

    const existingFilenames = new Set(images.map((img) => img.filename));
    const existingUrls = new Set(images.map((img) => img.url));
    const filtered = newImages.filter((img) => {
      const isDuplicateFilename = existingFilenames.has(img.filename);
      const isDuplicateUrl = existingUrls.has(img.url);
      return !isDuplicateFilename && !isDuplicateUrl;
    });

    const duplicatesCount = newImages.length - filtered.length;

    if (duplicatesCount > 0) {
      toast.warning(
        `${duplicatesCount} duplicate image${
          duplicatesCount > 1 ? 's' : ''
        } filtered out`
      );
    }

    if (filtered.length > 0) {
      dispatch(setImages([...images, ...filtered]));
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileDetails();
        if (res?.status === 200) {
          setProfileDetails(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch profile details:', err);
      }
    };
    fetchProfile();
  }, [dispatch]);

  const handleSketchGeneration = async () => {
    if (images.length > 20) {
      setModalImageCount(images.length);
      setShowImageLimitModal(true);
      return;
    }

    if (images.length < 20 && images.length > 0) {
      setModalImageCount(images.length);
      setShowLessThan20Modal(true);
      return;
    }

    await proceedWithGeneration();
  };

  const proceedWithGeneration = async () => {
    try {
      // Validation checks
      if (!activeBookId) {
        if (
          apiprofileDetails?.latestStepCode?.code === 'Upload_Images' &&
          !!profile?.data?.isOrderPlaces
        ) {
          setIsOpenBookSelectionModal(true);
        }
        return;
      }

      if (!coverImages?.length) {
        navigate('/dashboard?stepCode=Upload_Your_Cover_Photo');
        toast.error('Please choose a cover image first.');
        return;
      }

      if (!images?.length) {
        toast.error('Please choose some images first.');
        return;
      }

      // Check if images already generated
      const hasGeneratedImages = images.some(
        (img) => !!img?.meta?.aws_upload?.s3_url || !!img.AIUrl
      );

      if (hasGeneratedImages && (hasGeneratedImages?.length === images?.length)) {
        setNextDisabled(false);
        saveStep({
          userId: session?.user?._id,
          bookId: activeBookId,
          isCompleted: 'yes',
          stepCode: 'Design_Your_Cover_Page',
        });
        dispatch(
          markStepAsVisited({
            id: activeBookId,
            currentStepCode: 'Design_Your_Cover_Page',
          })
        );
        navigate(`/cover-image?stepCode=${'Design_Your_Cover_Page'}`);
        return;
      }

      // Get uploadable images (not already generated, not currently uploading)
      const uploadableImages = images.filter(
        img => !img.AIUrl && !uploadingImages.includes(img.id)
      );

      if (uploadableImages.length === 0) {
        setNextDisabled(false);
        saveStep({
          userId: session?.user?._id,
          bookId: activeBookId,
          isCompleted: 'yes',
          stepCode: 'Design_Your_Cover_Page',
        });
        dispatch(
          markStepAsVisited({
            id: activeBookId,
            currentStepCode: 'Design_Your_Cover_Page',
          })
        );
        navigate(`/cover-image?stepCode=${'Design_Your_Cover_Page'}`);
        return;
      }

      console.log('🚀 Starting lineart generation process...');
      setNextDisabled(true);

      // 🆕 Process images: check aspect ratio, crop if needed, upload blobs to Cloudinary
      const processedImageUrls = await processAllImagesForRun(uploadableImages);

      if (!processedImageUrls || processedImageUrls.length === 0) {
        throw new Error('Failed to process images');
      }

      console.log('✅ All images processed and ready for /run:', processedImageUrls);

      // Call backend API to start RunPod job (NO TITLE for lineart)
      const response = await run({
        imageUrls: processedImageUrls,
        bookId: activeBookId,
        // NO title field = lineart workflow
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to start job');
      }

      dispatch(setUploadingImages(uploadableImages.map(img => img.id)));

      console.log('✅ Lineart job started:', response.data);

      // Save step and navigate
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: 'Design_Your_Cover_Page',
      });

      dispatch(
        markStepAsVisited({
          id: activeBookId,
          currentStepCode: 'Design_Your_Cover_Page',
        })
      );

      navigate(`/cover-image?stepCode=${'Design_Your_Cover_Page'}`);

    } catch (err) {
      console.error('Error starting lineart generation:', err);
      toast.error(
        err.message || 'Failed to start image generation. Please try again.'
      );
      setNextDisabled(false);
    }
  };

  const handleUploadMore = (files) => {
    handleImageUpload(files);
    setShowLessThan20Modal(false);
  };

  const handleContinue = async () => {
    setShowLessThan20Modal(false);
    await proceedWithGeneration();
  };

  const handleDeleteImage = (id) => {
    if (uploadingImagesBatch) {
      toast.warning(
        "Cannot delete image while it's being processed. Please wait for generation to complete."
      );
      return;
    }

    const toDelete = images.find((img) => img.id === id || img._id === id);
    if (toDelete) {
      setDeletedFilenames((prev) => [...prev, toDelete.filename]);
      dispatch(deleteImage(id));
      removeBookImage(id);
    }
  };

  const onDragEnd = (images) => {
    const storedImages = images.filter((image) => !!image?._id);
    if (!!storedImages?.length) {
      reorderBooks({
        ordering_data: storedImages.map((item, i) => ({
          id: item._id,
          ordering: i,
        })),
      });
    }
  };

  return (
    <div className='relative'>
      <h1 className='text-2xl md:text-3xl xl:text-4xl font-bold text-black mb-2 md:mb-4 xl:mb-6 text-center flex gap-5 items-center justify-center flex-col-reverse md:flex-row'>
        Upload 20 Images <div className="h-6 md:h-9 flex items-center justify-center md:px-5 px-3 md:rounded-lg rounded-full border border-md-pink text-md-pink text-[10px] md:text-base">Step {stepNumber} of 5</div>
      </h1>
      <StepProgressView number={stepNumber}/>

      {!images.length && (
        <div className='bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-2 lg:gap-4 mb-6 w-full'>
          <div className='flex-shrink-0'>
            <Button
              buttonLabel='Back'
              buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
              buttonLabelClasses={"text-sm !font-bold"}
              buttonFunction={async () => {
                saveStep({
                  userId: session?.user?._id,
                  bookId: activeBookId,
                  isCompleted: 'yes',
                  stepCode: 'Upload_Your_Cover_Photo',
                });
                dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Your_Cover_Photo' }))
                navigate(`/dashboard?stepCode=${'Upload_Your_Cover_Photo'}`);
              }}
            />
          </div>

          <div className="flex-grow flex-shrink max-w-md text-black font-bold text-sm lg:text-base text-center hidden md:block">
            Please upload 20 additional images.
          </div>

          <div className='flex-shrink-0'>
            <Button
              buttonLabel='Next'
              buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
              buttonLabelClasses={"text-sm !font-bold"}
              buttonFunction={handleSketchGeneration}
              isDisable={images.length === 0}
            />
          </div>
        </div>
      )}

      {images.length > 0 && (
        <>
          <div className='bg-transparent md:bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-5 lg:gap-4 mb-6 w-full flex-wrap'>
            <div className="flex-shrink md:flex-shrink-0">
              <Button
                buttonLabel='Back'
                buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
                buttonLabelClasses={"text-sm !font-bold"}
                buttonFunction={async () => {
                  saveStep({
                    userId: session?.user?._id,
                    bookId: activeBookId,
                    isCompleted: 'yes',
                    stepCode: 'Upload_Your_Cover_Photo',
                  });
                  dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Your_Cover_Photo' }))
                  navigate(`/dashboard?stepCode=${'Upload_Your_Cover_Photo'}`);
                }}
              />
            </div>

            <div className="flex-grow flex-shrink-0 md:flex-shrink max-w-md order-last md:order-none">
              <div className='flex items-start md:items-center justify-center py-2 md:py-3 px-2 md:px-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-600 text-black font-bold gap-2'>
                <div className='flex items-center gap-1 md:gap-2'>
                  <i className='fa-solid fa-arrows-up-down-left-right text-base md:text-lg'></i>
                  <span className='text-sm lg:text-base'>
                    {uploadingImagesBatch ? 'Generating your pages...' : 'Drag to reorder pages.'}
                  </span>
                </div>

                {!uploadingImagesBatch && (
                  <div className='flex items-center gap-1 md:gap-2'>
                    <i className='fa-solid fa-crop-simple text-base md:text-lg'></i>
                    <span className='text-sm lg:text-base'>Click to crop</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-shrink md:flex-shrink-0">
              <Button
                buttonLabel={!!nextDisabled ? 'Generating...' : 'Next'}
                buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
                buttonLabelClasses={"text-sm !font-bold"}
                buttonFunction={handleSketchGeneration}
                isDisable={images.length === 0 || nextDisabled}
              />
            </div>
          </div>

          <div className='bg-white rounded-lg overflow-hidden py-2 md:py-4 px-3 md:px-6 relative'>
            <SortableImageGrid
              images={images}
              setImages={(updated) => dispatch(setImages(updated))}
              onEdit={(image) => {
                if (uploadingImagesBatch) {
                  toast.warning(
                    "Cannot edit image while it's being processed. Please wait for generation to complete."
                  );
                  return;
                }
                setCroppingImage(image);
              }}
              deletedFilenames={deletedFilenames}
              setDeletedFilenames={setDeletedFilenames}
              handleImageUpload={handleImageUpload}
              onDeleteImage={handleDeleteImage}
              onDragEnd={onDragEnd}
              hideImageUpload={false}
              isDisabled={uploadingImagesBatch}
              uploadingNewImages={uploadingImages}
            />
            {/* {renderLoadingOverlay()} */}
          </div>
        </>
      )}

      <div className='bg-white rounded-lg p-4 md:p-2.5'>
        <FileUploader onChange={handleImageUpload} single={false} />
      </div>

      {croppingImage && (
        <CropperModal
          isOpen={!!croppingImage}
          setIsOpen={() => setCroppingImage(null)}
          imageUrl={croppingImage.url}
          originalUrl={croppingImage.originalUrl}
          cropData={croppingImage.cropData}
          onCrop={handleCropSave}
          aspectRatio={8 / 11}
        />
      )}

      {!!profile?.myBooks?.length &&
        !activeBookId &&
        !!profile?.data?.isOrderPlaces && (
          <BookSelectionModal
            isOpen={isOpenBookSelectionModal}
            setIsOpen={setIsOpenBookSelectionModal}
            books={profile.myBooks ?? []}
            onBookSelect={(bookId) => dispatch(setActiveBookId({ bookId }))}
          />
        )}

      {/* Modals */}
      <ImageLimitModal
        isOpen={showImageLimitModal}
        onClose={() => setShowImageLimitModal(false)}
        currentCount={modalImageCount}
        limit={20}
      />

      <LessThan20Modal
        isOpen={showLessThan20Modal}
        onClose={() => setShowLessThan20Modal(false)}
        onContinue={handleContinue}
        onUploadMore={handleUploadMore}
        currentCount={modalImageCount}
        limit={20}
      />
    </div>
  );
};

export default PlaceOrderContent;